import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useState, useRef } from 'react'
import { useUIStore } from '@/store/ui'
import { useAuthStore } from '@/store/auth'
import { useIsAdmin } from '@/hooks/useIsAdmin'
import { fetchCoplayData } from '@/api/coplay'
import { clearGameCache } from '@/api/games'
import { GameCover } from '@/components/ui/GameCover'
import { Icon } from '@/components/ui/Icon'
import { Avatar } from '@/components/ui/Avatar'
import { CoopModesBadges, CoopPlayersBadge } from '@/components/ui/CoopBadges'
import { HltbBadge } from '@/components/ui/HltbBadge'
import { Spinner } from '@/components/ui/Spinner'
import { StarRating } from '@/components/ui/StarRating'
import { GroupHoursStat } from './GroupHoursStat'
import { BalanceBar } from './BalanceBar'
import { Heatmap } from './Heatmap'
import { PacingBar } from './PacingBar'
import { PlayingNudges } from './PlayingNudges'
import type { PlayingEntryWithGame, Friend } from '@/types'

interface PlayingCardV2Props {
  entry: PlayingEntryWithGame
  groupMembers: Friend[]
  onUpdate: (note: string) => void
  onPromoteToPlayed: () => void
  onDemoteToWishlist: () => void
  onRate: (rating: number) => void
  canEdit?: boolean
}

function relativeTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60_000)
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export function PlayingCardV2({ entry, groupMembers, onUpdate, onPromoteToPlayed, onDemoteToWishlist, onRate, canEdit = true }: PlayingCardV2Props) {
  const { activeGroupId } = useUIStore()
  const { user } = useAuthStore()
  const isAdmin = useIsAdmin()
  const queryClient = useQueryClient()
  const { game } = entry

  const myRating = (user && entry.ratings[user.steamId]) || 0
  const ratingValues = Object.values(entry.ratings).filter((v) => v > 0)
  const avgRating =
    ratingValues.length > 0
      ? Math.round((ratingValues.reduce((s, v) => s + v, 0) / ratingValues.length) * 10) / 10
      : null

  const clearCacheMutation = useMutation({
    mutationFn: () => clearGameCache(game.id),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['playing'] }),
  })

  const [editingNote, setEditingNote] = useState(false)
  const [noteValue, setNoteValue] = useState(entry.note)
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  function startEditNote() {
    setNoteValue(entry.note)
    setEditingNote(true)
    setTimeout(() => textareaRef.current?.focus(), 0)
  }

  function saveNote() {
    setEditingNote(false)
    if (noteValue !== entry.note) {
      onUpdate(noteValue)
    }
  }

  const { data: coplay, isLoading } = useQuery({
    queryKey: ['coplay', activeGroupId, entry.gameId],
    queryFn: () => fetchCoplayData(activeGroupId!, entry.gameId),
    enabled: !!activeGroupId,
    staleTime: 5 * 60 * 1000,
  })

  const startedAtTs = entry.startedAt ? new Date(entry.startedAt).getTime() / 1000 : null
  const localOwner = coplay?.memberContribs.find((c) => c.steamId === coplay.localCoplayOwnerId)

  return (
    <div className="cg-playing-card cg-playing-card-rich">
      <div className="cg-playing-rich-top">
        <div className="cg-playing-cover">
          <GameCover game={game} size="lg" />
        </div>

        <div className="cg-playing-rich-meta">
          <div style={{ fontWeight: 600, fontSize: 17, color: 'var(--fg-0)' }}>{game.title}</div>
          <div style={{ fontSize: 12, color: 'var(--fg-3)' }}>
            {entry.startedAt && `Started ${relativeTime(entry.startedAt)}`}
          </div>
          <div className="cg-coop compact">
            <CoopPlayersBadge game={game} groupSize={coplay?.memberContribs.length ?? null} />
            <HltbBadge game={game} />
            <CoopModesBadges coop={game.coop} compact />
          </div>
          {coplay?.localCoplayLikely && (
            <div
              className="cg-local-coplay"
              title="Only one group member is known to own this local co-op game, so their Steam playtime is treated as group play."
            >
              <Icon name="controller" size={12} />
              Likely local co-op{localOwner?.name ? ` via ${localOwner.name}` : ''}
            </div>
          )}

          {editingNote ? (
            <textarea
              ref={textareaRef}
              value={noteValue}
              onChange={(e) => setNoteValue(e.target.value)}
              onBlur={saveNote}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  saveNote()
                }
                if (e.key === 'Escape') {
                  setEditingNote(false)
                  setNoteValue(entry.note)
                }
              }}
              placeholder="Add a session note…"
              style={{
                width: '100%',
                resize: 'none',
                background: 'var(--bg-2)',
                border: '1px solid var(--accent)',
                borderRadius: 4,
                padding: '7px 10px',
                fontSize: 12,
                color: 'var(--fg-1)',
                fontFamily: 'inherit',
                lineHeight: 1.4,
                marginTop: 8,
              }}
              rows={3}
            />
          ) : entry.note ? (
            <div
              className="cg-playing-note"
              onClick={canEdit ? startEditNote : undefined}
              style={{ cursor: canEdit ? 'pointer' : 'default', marginTop: 8 }}
              title={canEdit ? 'Click to edit' : undefined}
            >
              "{entry.note}"
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              fontSize: 11.5,
              color: 'var(--fg-3)',
              marginTop: 10,
            }}
          >
            <span style={{ color: 'var(--fg-2)' }}>Your rating</span>
            <StarRating value={myRating} onChange={onRate} size={13} ariaLabel="Your rating" />
            {avgRating !== null && (
              <span className="cg-avg-wrap" tabIndex={0}>
                · avg{' '}
                <span style={{ color: 'var(--fg-1)', fontWeight: 600 }}>{avgRating.toFixed(1)}</span>{' '}
                ({ratingValues.length} rated)
                <div className="cg-avg-pop" onClick={(e) => e.stopPropagation()}>
                  {groupMembers
                    .filter((m) => (entry.ratings[m.id] ?? 0) > 0)
                    .map((member) => (
                      <div key={member.id} className="cg-played-rate-row">
                        <Avatar friend={member} size={18} showRing={false} />
                        <span className="cg-played-rate-name">{member.nickname ?? member.name}</span>
                        <StarRating value={entry.ratings[member.id] ?? 0} readOnly size={12} />
                      </div>
                    ))}
                </div>
              </span>
            )}
          </div>

          <div className="cg-playing-actions" style={{ marginTop: 'auto', paddingTop: 8 }}>
            {canEdit && (
              <>
                <button className="cg-btn cg-btn-primary" data-action="played" onClick={onPromoteToPlayed}>
                  <Icon name="star" size={11} /> Mark played
                </button>
                <button className="cg-btn" data-action="wishlist" onClick={onDemoteToWishlist}>
                  <Icon name="wishlist" size={11} />
                  Back to wishlist
                </button>
                {!entry.note && !editingNote && (
                  <button className="cg-btn" onClick={startEditNote}>
                    <Icon name="edit" size={11} /> Add note
                  </button>
                )}
              </>
            )}
            {isAdmin && (
              <button
                className="cg-btn ghost tiny"
                onClick={() => clearCacheMutation.mutate()}
                disabled={clearCacheMutation.isPending}
              >
                {clearCacheMutation.isPending ? 'Clearing…' : 'Clear cache'}
              </button>
            )}
          </div>
        </div>

        <div className="cg-playing-rich-stat">
          {isLoading && <Spinner />}
          {coplay && <GroupHoursStat coplay={coplay} />}
        </div>
      </div>

      {coplay && canEdit && (
        <PlayingNudges
          coplay={coplay}
          gameTitle={game.title}
          onPromoteToPlayed={onPromoteToPlayed}
          onDemoteToWishlist={onDemoteToWishlist}
        />
      )}

      {coplay && (
        <div className="cg-playing-rich-grid">
          <BalanceBar
            contribs={coplay.memberContribs}
            currentSteamId={user?.steamId ?? ''}
          />
          <Heatmap
            heatmap={coplay.heatmap}
            contribs={coplay.memberContribs}
            dataStartedAt={coplay.dataStartedAt}
            startedAt={startedAtTs}
            currentSteamId={user?.steamId ?? ''}
          />
        </div>
      )}

      {coplay && <PacingBar coplay={coplay} />}
    </div>
  )
}
